import { useStatsigClient } from '@statsig/react-bindings';

/**
 * Analytics metadata is intentionally loosely typed — Statsig coerces
 * values to strings on the wire. Loose typing keeps callers from fighting
 * the type system when surfacing optional fields.
 */
export type AnalyticsEventMetadata = Record<string, unknown>;

/**
 * Module-level reference to the Statsig client, set by StatsigClientProvider
 * once initialization completes. This lets non-React code paths (axios
 * interceptors, Redux middleware, error boundaries) log events without
 * needing to live inside the React tree.
 *
 * Outside-React code should use logAnalyticsEvent directly.
 * Inside-React components should use the useAnalytics() hook.
 */
type StatsigClientLike = {
  logEvent: (
    eventName: string,
    value?: string | number,
    metadata?: Record<string, string>
  ) => void;
  checkGate: (gate: string) => boolean;
};

let _client: StatsigClientLike | null = null;

/** Called by StatsigClientProvider when the SDK becomes ready. */
export function registerAnalyticsClient(client: StatsigClientLike | null): void {
  _client = client;
}

/**
 * Log an analytics event. Safe to call before Statsig has initialized
 * (becomes a noop) and safe to call when Statsig is fully disabled
 * (REACT_APP_STATSIG_CLIENT_KEY unset). Events fired before init is
 * complete are silently dropped — auto-capture covers most early activity.
 */
export function logAnalyticsEvent(
  eventName: string,
  value?: string | number,
  metadata?: AnalyticsEventMetadata
): void {
  if (!_client) return;
  try {
    _client.logEvent(
      eventName,
      value,
      metadata as Record<string, string> | undefined
    );
  } catch {
    // Never let analytics break caller flow.
  }
}

/**
 * Check a Statsig feature gate. Safe to call before Statsig has initialized
 * (returns `defaultValue`, which is `false`) and when Statsig is fully
 * disabled (REACT_APP_STATSIG_CLIENT_KEY unset). Use in non-React code paths
 * that must branch on a gate without access to React hooks.
 */
export function checkFeatureGate(gateName: string, defaultValue = false): boolean {
  if (!_client) return defaultValue;
  try {
    return _client.checkGate(gateName);
  } catch {
    return defaultValue;
  }
}

/**
 * Hook flavor for use inside React components. Returns the same logger
 * function as logAnalyticsEvent — calling useStatsigClient subscribes the
 * component to context updates, ensuring the function reference is stable
 * relative to the SDK lifecycle.
 */
export function useAnalytics(): typeof logAnalyticsEvent {
  useStatsigClient();
  return logAnalyticsEvent;
}
